import React, { useState, useEffect } from 'react';
import { Pin } from 'lucide-react';

const TableOfContents = ({ toc, mobileIsOpen = false, onMobileToggle }) => {
  const [activeId, setActiveId] = useState('');
  const [isOpen, setIsOpen] = useState(true);
  const [isPinned, setIsPinned] = useState(true);

  // Load pinned state from localStorage (defaults to true/pinned on desktop)
  useEffect(() => {
    const savedState = localStorage.getItem('toc-pinned');
    if (savedState !== null) {
      const pinned = savedState === 'true';
      setIsPinned(pinned);
      setIsOpen(pinned);
    }
  }, []);

  // Save pinned state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('toc-pinned', isPinned.toString());
  }, [isPinned]);

  useEffect(() => {
    // Create intersection observer to track visible sections
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-100px 0px -66%',
        threshold: 0
      }
    );

    // Observe all headings
    const headings = document.querySelectorAll('h2[id], h3[id]');
    headings.forEach((heading) => observer.observe(heading));

    return () => {
      headings.forEach((heading) => observer.unobserve(heading));
    };
  }, []);

  const handleClick = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleMouseEnter = () => {
    if (!isPinned) {
      setIsOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isPinned) {
      setIsOpen(false);
    }
  };

  const togglePin = () => {
    setIsPinned(!isPinned);
    if (!isPinned) {
      setIsOpen(true);
    }
  };

  if (!toc || toc.length === 0) {
    return null;
  }

  return (
    <>
      {/* Desktop: Hover trigger area + Sidebar */}
      <div
        className="hidden xl:block fixed left-0 top-0 bottom-0 z-50"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Hover trigger area (thin strip) */}
        <div className="absolute left-0 top-0 bottom-0 w-2 bg-transparent" />

        {/* Sidebar */}
        <aside
          className={`
            absolute left-0 top-0 bottom-0
            bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm
            border-r border-gray-200 dark:border-gray-700
            transition-all duration-300 ease-in-out
            ${isOpen ? 'w-56 opacity-100' : 'w-0 opacity-0'}
          `}
        >
          <div className={`h-full flex flex-col ${isOpen ? 'visible' : 'invisible'}`}>
            {/* Header with Pin button */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700" style={{ paddingBottom: '1rem' }}>
              <h2 className="text-sm font-bold uppercase tracking-widest text-gray-900 dark:text-gray-200">
                On This Page
              </h2>
              <button
                onClick={togglePin}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label={isPinned ? "Unpin table of contents" : "Pin table of contents"}
                title={isPinned ? "Unpin (will hide on hover out)" : "Pin (keep open)"}
              >
                <Pin
                  className={`w-5 h-5 transition-colors ${isPinned ? 'text-blue-500' : 'text-gray-500'}`}
                  fill={isPinned ? 'currentColor' : 'none'}
                />
              </button>
            </div>

            {/* TOC Items */}
            <nav className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-blue-500 dark:scrollbar-thumb-blue-400 scrollbar-track-transparent scrollbar-thumb-rounded">
              <ul className="space-y-2">
                {toc.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => handleClick(e, item.id)}
                      className={`
                        block text-sm py-1.5 px-3 rounded
                        border-l-2 transition-all duration-200
                        ${
                          activeId === item.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-semibold'
                            : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }
                      `}
                    >
                      {item.text}
                    </a>
                    {item.children && item.children.length > 0 && (
                      <ul className="mt-1 ml-4 space-y-1">
                        {item.children.map((child) => (
                          <li key={child.id}>
                            <a
                              href={`#${child.id}`}
                              onClick={(e) => handleClick(e, child.id)}
                              className={`
                                block text-xs py-1 px-3 rounded
                                border-l-2 transition-all duration-200
                                ${
                                  activeId === child.id
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-semibold'
                                    : 'border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                }
                              `}
                            >
                              {child.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </aside>

        {/* Hover hint indicator (shows when closed) */}
        {!isOpen && !isPinned && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-16 bg-blue-500/30 rounded-r transition-all duration-200 hover:w-1.5 hover:bg-blue-500/50" />
        )}
      </div>

      {/* Mobile Drawer */}
      <aside
        className={`
          xl:hidden
          fixed left-0 top-0 bottom-0 z-30
          w-56
          bg-white dark:bg-gray-900
          border-r border-gray-200 dark:border-gray-700
          transition-transform duration-300 ease-in-out
          ${mobileIsOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-900 dark:text-gray-200">
              On This Page
            </h2>
            <button
              onClick={onMobileToggle}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Close table of contents"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* TOC Items */}
          <nav className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-blue-500 dark:scrollbar-thumb-blue-400 scrollbar-track-transparent scrollbar-thumb-rounded">
            <ul className="space-y-2">
              {toc.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    onClick={(e) => handleClick(e, item.id)}
                    className={`
                      block text-sm py-1.5 px-3 rounded
                      border-l-2 transition-all duration-200
                      ${
                        activeId === item.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-semibold'
                          : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }
                    `}
                  >
                    {item.text}
                  </a>
                  {item.children && item.children.length > 0 && (
                    <ul className="mt-1 ml-4 space-y-1">
                      {item.children.map((child) => (
                        <li key={child.id}>
                          <a
                            href={`#${child.id}`}
                            onClick={(e) => handleClick(e, child.id)}
                            className={`
                              block text-xs py-1 px-3 rounded
                              border-l-2 transition-all duration-200
                              ${
                                activeId === child.id
                                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 font-semibold'
                                  : 'border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                              }
                            `}
                          >
                            {child.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </aside>

      {/* Mobile Backdrop */}
      {mobileIsOpen && (
        <div
          className="xl:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-20"
          onClick={onMobileToggle}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default TableOfContents;
